import React, { useRef, useEffect } from 'react';
import useStyles from './AutoResizeTextArea.styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
/**
 * @typedef {Object} AutoResizeTextareaProps
 * @property {function} onChange - Callback function triggered on textarea value change.
 * @property {string} value - The current value of the textarea.
 * @property {string} [className] - Additional class names for styling.
 * @property {Object} [props] - Additional props to be passed to the textarea.
 */

/**
 * AutoResizeTextarea is a React component that automatically adjusts its height
 * based on the content.
 *
 * @param {AutoResizeTextareaProps} props - The props for the component.
 * @returns {JSX.Element} The rendered textarea component.
 */
const AutoResizeTextarea = ({ onChange, value, className, ...props }) => {
  const textareaRef = useRef(null);

  const { textarea } = useStyles();

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [value]);

  return (
    <textarea
      className={clsx(textarea, className)}
      ref={textareaRef}
      value={value}
      onChange={onChange}
      rows={1}
      style={{
        overflow: 'hidden',
        resize: 'none',
      }}
      {...props}
    />
  );
};

AutoResizeTextarea.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  className: PropTypes.string,
};

AutoResizeTextarea.defaultProps = {
  className: '',
};

export default AutoResizeTextarea;
